import type { AnalyticsField, AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';

/** A relative date-range preset. `mtd` is month-to-date (UTC month boundary). */
export type DateRangePreset = 'last-7' | 'last-30' | 'last-90' | 'last-12mo' | 'mtd';

/**
 * The global date range. `kind: 'preset'` carries a rolling window resolved
 * against "now" at query time; `kind: 'custom'` carries explicit inclusive
 * `start`/`end` calendar days (`YYYY-MM-DD`).
 */
export interface GlobalDateRange {
    kind: 'preset' | 'custom';
    preset: DateRangePreset | null;
    start: string | null;
    end: string | null;
}

/**
 * The dimensions carried in the global tier — the filters that mean the same
 * thing on every page and ride along page-to-page. Everything else is a
 * page-local sub-filter.
 */
export type GlobalFilterField = 'provider_connection_id' | 'user_id';

/** A snapshot of the whole global filter state — the unit the URL codec round-trips. */
export interface GlobalFilterSnapshot {
    dateRange: GlobalDateRange;
    dimensions: AnalyticsFilter[];
}

export const GLOBAL_FILTER_FIELDS: GlobalFilterField[] = ['provider_connection_id', 'user_id'];

export const DEFAULT_DATE_RANGE: GlobalDateRange = { kind: 'preset', preset: 'last-90', start: null, end: null };

export const DATE_RANGE_PRESETS: { value: DateRangePreset; label: string }[] = [
    { value: 'last-7', label: 'Last 7 days' },
    { value: 'last-30', label: 'Last 30 days' },
    { value: 'last-90', label: 'Last 90 days' },
    { value: 'last-12mo', label: 'Last 12 months' },
    { value: 'mtd', label: 'Month to date' },
];

/** True when the field is one of the global-tier dimensions. */
export function isGlobalFilterField(field: AnalyticsField): field is GlobalFilterField {
    return (GLOBAL_FILTER_FIELDS as string[]).includes(field);
}

export function emptyGlobalFilterSnapshot(): GlobalFilterSnapshot {
    return { dateRange: { ...DEFAULT_DATE_RANGE }, dimensions: [] };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Resolves a {@link GlobalDateRange} to concrete `[start, end)` bounds. Presets
 * roll back from `now`; `mtd` starts at the UTC month boundary so cost records
 * stamped at UTC month starts are not excluded for UTC-negative timezones.
 */
export function resolveDateRange(range: GlobalDateRange, now: Date = new Date()): { start: Date; end: Date } {
    if (range.kind === 'custom' && range.start !== null && range.end !== null) {
        const start = new Date(`${range.start}T00:00:00.000Z`);
        const end = new Date(new Date(`${range.end}T00:00:00.000Z`).getTime() + DAY_MS);
        return { start, end };
    }
    const end = now;
    const start = new Date(end.getTime());
    switch (range.preset ?? DEFAULT_DATE_RANGE.preset) {
        case 'last-7':
            start.setTime(end.getTime() - 7 * DAY_MS);
            break;
        case 'last-30':
            start.setTime(end.getTime() - 30 * DAY_MS);
            break;
        case 'last-90':
            start.setTime(end.getTime() - 90 * DAY_MS);
            break;
        case 'last-12mo':
            start.setMonth(start.getMonth() - 12);
            break;
        case 'mtd':
            start.setTime(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
            break;
    }
    return { start, end };
}

/** Whole days spanned by a resolved range — used to pick a `days`-window API argument. */
export function resolveWindowDays(range: GlobalDateRange, now: Date = new Date()): number {
    const { start, end } = resolveDateRange(range, now);
    return Math.max(1, Math.ceil((end.getTime() - start.getTime()) / DAY_MS));
}
